import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useMemo, useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Map as MapIcon, LayoutGrid } from "lucide-react";

// A curated seed of city → coordinates. Extra emphasis on Texas + global coverage.
const CITY_COORDS = {
  // Texas
  "Austin, USA":         [30.2672, -97.7431],
  "Houston, USA":        [29.7604, -95.3698],
  "Dallas, USA":         [32.7767, -96.7970],
  "San Antonio, USA":    [29.4241, -98.4936],
  "Fort Worth, USA":     [32.7555, -97.3308],
  "El Paso, USA":        [31.7619, -106.4850],
  "Marfa, USA":          [30.3095, -104.0206],
  "Galveston, USA":      [29.3013, -94.7977],
  "Waco, USA":           [31.5493, -97.1467],
  "Corpus Christi, USA": [27.8006, -97.3964],
  // North America
  "New York, USA":       [40.7238, -73.9990],
  "Malibu, USA":         [34.0259, -118.7798],
  "Los Angeles, USA":    [34.0522, -118.2437],
  "San Francisco, USA":  [37.7749, -122.4194],
  "Miami, USA":          [25.7617, -80.1918],
  "Chicago, USA":        [41.8781, -87.6298],
  "Seattle, USA":        [47.6062, -122.3321],
  "Nashville, USA":      [36.1627, -86.7816],
  "Aspen, USA":          [39.1911, -106.8175],
  "Portland, USA":       [45.5152, -122.6784],
  "Boston, USA":         [42.3601, -71.0589],
  "New Orleans, USA":    [29.9511, -90.0715],
  "Toronto, Canada":     [43.6532, -79.3832],
  "Vancouver, Canada":   [49.2827, -123.1207],
  "Mexico City, Mexico": [19.4326, -99.1332],
  "Tulum, Mexico":       [20.2114, -87.4654],
  // Europe
  "London, UK":          [51.5074, -0.1278],
  "Paris, France":       [48.8566, 2.3522],
  "Chamonix, France":    [45.9237, 6.8694],
  "Barcelona, Spain":    [41.3851, 2.1734],
  "Madrid, Spain":       [40.4168, -3.7038],
  "Amsterdam, Netherlands":[52.3676, 4.9041],
  "Rome, Italy":         [41.9028, 12.4964],
  "Milan, Italy":        [45.4642, 9.1900],
  "Florence, Italy":     [43.7696, 11.2558],
  "Lisbon, Portugal":    [38.7223, -9.1393],
  "Berlin, Germany":     [52.5200, 13.4050],
  "Vienna, Austria":     [48.2082, 16.3738],
  "Copenhagen, Denmark": [55.6761, 12.5683],
  "Reykjavik, Iceland":  [64.1466, -21.9426],
  "Athens, Greece":      [37.9838, 23.7275],
  "Santorini, Greece":   [36.3932, 25.4615],
  "Zurich, Switzerland": [47.3769, 8.5417],
  // Asia
  "Tokyo, Japan":        [35.6762, 139.6503],
  "Kyoto, Japan":        [35.0116, 135.7681],
  "Osaka, Japan":        [34.6937, 135.5023],
  "Seoul, South Korea":  [37.5665, 126.9780],
  "Bangkok, Thailand":   [13.7563, 100.5018],
  "Bali, Indonesia":     [-8.4095, 115.1889],
  "Singapore, Singapore":[1.3521, 103.8198],
  "Hong Kong, China":    [22.3193, 114.1694],
  "Shanghai, China":     [31.2304, 121.4737],
  "Mumbai, India":       [19.0760, 72.8777],
  "Dubai, UAE":          [25.2048, 55.2708],
  // Oceania & Africa & LatAm
  "Sydney, Australia":   [-33.8688, 151.2093],
  "Melbourne, Australia":[-37.8136, 144.9631],
  "Auckland, New Zealand":[-36.8485, 174.7633],
  "Queenstown, New Zealand":[-45.0312, 168.6626],
  "Cape Town, South Africa":[-33.9249, 18.4241],
  "Marrakech, Morocco":  [31.6295, -7.9811],
  "Rio de Janeiro, Brazil":[-22.9068, -43.1729],
  "Buenos Aires, Argentina":[-34.6037, -58.3816],
  "Lima, Peru":          [-12.0464, -77.0428],
};

// Country-centroid fallback so hosts in cities we don't know still show a pin.
const COUNTRY_COORDS = {
  "USA":         [39.8283, -98.5795],
  "US":          [39.8283, -98.5795],
  "Canada":      [56.1304, -106.3468],
  "Mexico":      [23.6345, -102.5528],
  "UK":          [55.3781, -3.4360],
  "France":      [46.2276,   2.2137],
  "Spain":       [40.4637,  -3.7492],
  "Italy":       [41.8719,  12.5674],
  "Germany":     [51.1657,  10.4515],
  "Netherlands": [52.1326,   5.2913],
  "Portugal":    [39.3999,  -8.2245],
  "Greece":      [39.0742,  21.8243],
  "Switzerland": [46.8182,   8.2275],
  "Austria":     [47.5162,  14.5501],
  "Denmark":     [56.2639,   9.5018],
  "Iceland":     [64.9631, -19.0208],
  "Japan":       [36.2048, 138.2529],
  "South Korea": [35.9078, 127.7669],
  "Thailand":    [15.8700, 100.9925],
  "Indonesia":   [-0.7893, 113.9213],
  "Singapore":   [1.3521, 103.8198],
  "China":       [35.8617, 104.1954],
  "India":       [20.5937,  78.9629],
  "UAE":         [23.4241,  53.8478],
  "Australia":   [-25.2744, 133.7751],
  "New Zealand": [-40.9006, 174.8860],
  "South Africa":[-30.5595,  22.9375],
  "Morocco":     [31.7917,  -7.0926],
  "Brazil":      [-14.2350, -51.9253],
  "Argentina":   [-38.4161, -63.6167],
  "Peru":        [-9.1900,  -75.0152],
};

function coordsFor(p) {
  if (p.latitude && p.longitude) return [p.latitude, p.longitude];
  const key = `${p.city}, ${p.country}`;
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  if (p.country && COUNTRY_COORDS[p.country]) return COUNTRY_COORDS[p.country];
  return null;
}

// Custom emerald marker
const emeraldIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:28px;height:28px;border-radius:14px 14px 14px 0;background:#10B981;border:2px solid #ffffff;box-shadow:0 4px 12px rgba(0,0,0,0.2);transform:rotate(-45deg);display:flex;align-items:center;justify-content:center"><div style="width:10px;height:10px;border-radius:50%;background:#ffffff;transform:rotate(45deg)"></div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    // Force layout recalculation now that the container is rendered + sized.
    map.invalidateSize();
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 5); return; }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 5, animate: false });
  }, [map, points]);
  return null;
}

export function CertifiedMapView({ items }) {
  const withCoords = useMemo(() => items.map((p) => ({ p, c: coordsFor(p) })).filter((x) => x.c), [items]);
  const noCoords = items.length - withCoords.length;
  const points = useMemo(() => withCoords.map((x) => x.c), [withCoords]);
  const bounds = points.length > 0 ? L.latLngBounds(points) : null;
  const mapKey = points.map((c) => `${c[0].toFixed(2)},${c[1].toFixed(2)}`).join("|") || "empty";

  return (
    <div className="relative">
      <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white" style={{ height: "560px" }} data-testid="map-container">
        <MapContainer
          key={mapKey}
          bounds={bounds || undefined}
          center={bounds ? undefined : [20, 0]}
          zoom={bounds ? undefined : 2}
          boundsOptions={{ padding: [40, 40], maxZoom: 5 }}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {withCoords.map(({ p, c }) => (
            <Marker key={p.id} position={c} icon={emeraldIcon}>
              <Popup>
                <div style={{ fontFamily: "Outfit, Arial, sans-serif", minWidth: 200 }}>
                  {p.photos?.[0] && <img src={p.photos[0]} alt="" style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8 }} />}
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#0A1128", marginTop: 8 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{p.city}, {p.country}</div>
                  <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#10B981" }}>{p.freshness_score}<span style={{ fontSize: 12, color: "#94A3B8" }}> /100</span></span>
                    <Link to={`/property/${p.id}`} style={{ background: "#0A1128", color: "#fff", padding: "6px 12px", borderRadius: 999, fontSize: 12, textDecoration: "none" }}>View</Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {noCoords > 0 && (
        <div className="mt-3 text-xs text-slate-500 text-center">
          {noCoords} certified {noCoords === 1 ? "stay is" : "stays are"} pending coordinates and not yet shown on the map.
        </div>
      )}
    </div>
  );
}

export function ViewToggle({ view, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="view-toggle">
      <button
        onClick={() => onChange("grid")}
        data-testid="view-grid"
        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "grid" ? "bg-[#0A1128] text-white" : "text-slate-600 hover:text-emerald-700"}`}
      >
        <LayoutGrid className="w-3.5 h-3.5" /> Grid
      </button>
      <button
        onClick={() => onChange("map")}
        data-testid="view-map"
        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "map" ? "bg-[#0A1128] text-white" : "text-slate-600 hover:text-emerald-700"}`}
      >
        <MapIcon className="w-3.5 h-3.5" /> Map
      </button>
    </div>
  );
}

export default CertifiedMapView;
